import { apiSlice } from "../store/app.api";
import { supabase } from "../utils/API";

const cinemaApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getCinemas: build.query<
      any,
      {
        start: number;
        end: number;
        hasMall?: "with" | "without";
        mallId?: string | number;
        searchTerm?: string;
      }
    >({
      queryFn: async ({ start, end, hasMall, mallId, searchTerm }) => {
        let query = supabase
          .from("theaters")
          .select(
            `
              *,
              theatre_group:theatre_groups(*)
            `,
            { count: "exact" },
          )
          .order("name", { ascending: true })
          .range(start, end);

        if (hasMall === "with") {
          query = query.not("mall_id", "is", null);
        } else if (hasMall === "without") {
          query = query.is("mall_id", null);
        }

        if (mallId) {
          query = query.eq("mall_id", mallId);
        }

        if (searchTerm?.trim()) {
          const s = searchTerm.trim();

          const normalizedStatus =
            s.toLowerCase() === "active"
              ? true
              : s.toLowerCase() === "inactive"
                ? false
                : null;

          const typeKeywords = ["REGULAR", "SPECIAL"];
          const upperSearch = s.toUpperCase();

          if (normalizedStatus !== null && typeKeywords.includes(upperSearch)) {
            query = query.or(
              `name.ilike.%${s}%,type.ilike.%${s}%,is_active.eq.${normalizedStatus}`,
            );
          } else if (normalizedStatus !== null) {
            query = query.or(
              `name.ilike.%${s}%,is_active.eq.${normalizedStatus}`,
            );
          } else if (typeKeywords.includes(upperSearch)) {
            query = query.or(`name.ilike.%${s}%,type.ilike.%${s}%`);
          } else {
            query = query.ilike("name", `%${s}%`);
          }
        }

        const { data, count, error } = await query;

        if (error) return { error };

        return {
          data: {
            list: data ?? [],
            count: count ?? 0,
          },
        };
      },
      providesTags: ["Cinema"],
    }),

    getCinemaById: build.query<any, string>({
      queryFn: async (id: string) => {
        if (!/^\d+$/.test(id)) {
          return { error: { message: `Invalid theatre id: ${id}` } as any };
        }

        const { data, error } = await supabase
          .from("theaters")
          .select(
            `
          *,
          theatre_group:theatre_groups(*)
        `,
          )
          .eq("id", Number(id))
          .single();

        if (error) return { error };
        return { data };
      },
      providesTags: ["Cinema"],
    }),

    getUnregisteredCinemas: build.query<any[], void>({
      queryFn: async () => {
        const currentYear = new Date().getFullYear();

        const { data, error } = await supabase
          .from("theaters")
          .select("id, name, last_reg_year")
          .eq("is_active", true)
          .neq("last_reg_year", currentYear)
          .order("name", { ascending: true });

        if (error) return { error };
        return { data };
      },
    }),

    addCinema: build.mutation<any, any>({
      queryFn: async (cinema) => {
        const { data, error } = await supabase.from("cinema").insert(cinema);
        if (error) return { error };
        return { data };
      },
      invalidatesTags: ["Cinema"],
    }),

    deleteCinema: build.mutation<any, any>({
      queryFn: async (id) => {
        const { data, error } = await supabase
          .from("cinema")
          .delete()
          .eq("id", Number(id));

        if (error) return { error };
        return { data };
      },
      invalidatesTags: ["Cinema"],
    }),

    getCinemaMovies: build.query<any, any>({
      queryFn: async (cinemaId) => {
        const { data, error } = await supabase
          .from("cinema_movies")
          .select(
            `
              movies (
                *
              )
            `,
          )
          .eq("cinema_id", cinemaId);

        if (error) return { error };
        return { data: data?.map((entry) => entry.movies) };
      },
      providesTags: ["Cinema"],
    }),

    updateTheatre: build.mutation<any, any>({
      queryFn: async (theatre: any) => {
        const { id, ...rest } = theatre;

        if (!id) {
          return { error: { message: "ID is required for update" } };
        }

        const { data, error } = await supabase
          .from("theaters")
          .update(rest)
          .eq("id", id)
          .select()
          .single();

        if (error) return { error };
        return { data };
      },
      invalidatesTags: ["Cinema", "mall"],
    }),

    updateMallOfTheatre: build.mutation<any, any>({
      queryFn: async ({ cinema_id, mall_id }) => {
        const { data, error } = await supabase
          .from("theaters")
          .update({ mall_id })
          .eq("id", cinema_id)
          .select()
          .single();

        if (error) return { error };
        return { data };
      },
      invalidatesTags: ["Cinema", "mall"],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetCinemasQuery,
  useLazyGetCinemasQuery,
  useAddCinemaMutation,
  useDeleteCinemaMutation,
  useGetCinemaMoviesQuery,
  useUpdateTheatreMutation,
  useUpdateMallOfTheatreMutation,
  useGetCinemaByIdQuery,
  useLazyGetUnregisteredCinemasQuery,
} = cinemaApi;
